import images from '@assets/images/index'
import GymCharts from '@components/Charts/GoalIndex/GymCharts'
import StickerCharts from '@components/Charts/GoalIndex/StickerCharts'
import { useQuery } from '@tanstack/react-query'
import { Col, Flex, Row, Spin } from 'antd'
import { useEffect, useState } from 'react'

import { Link } from '@suntex.core/nav'
import { Dec, Frc, Gyms, Int, K2, Num } from '@suntex.fim/api-client'
import { gradeToGymLevel } from '@suntex.fim/assessment'
import {
  $EntityLevel,
  $GymMode,
  EntityLevel,
  GoalsIndexPhase,
  GymMode,
  SpecialGradeLevel
} from '@suntex.fim/enums'
import { useFramework } from '@suntex.fim/framework'
import { useEntityContext } from '@suntex.fim/nav'
import { LevelSelect, Tooltip, useTheme } from '@suntex.fim/ui'

import { GymData } from '../Module/gymsUtils'

const modeOptions = $GymMode.listOptions([
  GymMode.WholeNumbers,
  GymMode.Fractions,
  GymMode.Decimals,
  GymMode.Integers,
  GymMode.K2
])

function Gym({
  phase,
  id,
  year,
  grade,
  defaultGrade,
  entityLevel,
  gymLevel,
  onLevelChange
}: {
  phase: GoalsIndexPhase
  id?: number
  year?: string
  grade?: SpecialGradeLevel
  defaultGrade?: SpecialGradeLevel
  entityLevel?: number
  gymLevel?: GymMode | null
  onLevelChange?: (level: GymMode) => void // Callback for level changes
}) {
  const { api } = useFramework()
  const theme = useTheme()
  const [level, setLevel] = useState<GymMode>(gymLevel ?? GymMode.WholeNumbers)
  const [gymData, setGymData] = useState<Gyms | null>(null)
  const [averageStickers, setAverageStickers] = useState(0)
  const { entityLevel: apiLevel, entityId: apiId } = useEntityContext()
  const role = $EntityLevel.describe(apiLevel).slug
  const { data } = useQuery({
    queryKey: ['assessment', 'goalsInfoGyms', apiId, apiLevel, year, grade],
    queryFn: () =>
      api.get_Assessment_GoalsInfoGyms({
        id: apiId!,
        level: apiLevel,
        year: year!,
        grade
      }),
    enabled: apiId != null && year != null
  })

  useEffect(() => {
    if (data) {
      setGymData(data.gyms)
      setAverageStickers(data.stickers || 0)
    }
  }, [data])

  useEffect(() => {
    const g = defaultGrade ?? grade
    if (g != null && gymLevel === null) setLevel(gradeToGymLevel(g))
  }, [defaultGrade, grade])

  useEffect(() => {
    if (onLevelChange) {
      if (level !== null) {
        onLevelChange(level) // Notify parent of level changes
      }
    }
  }, [level, onLevelChange])

  const { slug: gymModeSlug, steps } = $GymMode.describe(level)

  //if no data yet, show loading state.
  if (!gymData) {
    return <Spin />
  }

  const levelData: GymData = gymData[gymModeSlug]

  return (
    <>
      <Flex vertical gap="20px" className="graph-tiles team-activity">
        <Flex
          align="center"
          className="graph-tiles__title"
          justify="space-between"
        >
          {entityLevel !== EntityLevel.Player ? (
            <>
              <Link
                className="report-link"
                to={`/${role}/${id}/assessment-report/sticker-count`}
              >
                <h4 className="title">Activity - Sticker Count</h4>
              </Link>
              <Link
                to={`/${role}/${id}/assessment-report/sticker-count`}
                className="print-hidden"
              >
                <Tooltip
                  classNames={{ root: 'progress-tooltip' }}
                  title={
                    <div>
                      Click here to drill down into the Sticker Count report for
                      this {role}
                    </div>
                  }
                  arrow={false}
                  placement="bottom"
                >
                  <img
                    src={images.Activity}
                    alt="Gyms"
                    className="goal-index-report-icon"
                  />
                </Tooltip>
              </Link>
            </>
          ) : (
            <h4 className="title">Activity - Sticker Count</h4>
          )}
        </Flex>
        <Flex vertical align="center" className="graph-tiles__graph">
          <div className="graph-container">
            <StickerCharts
              graphColor={theme.colors.stickers}
              value={averageStickers}
            />
          </div>
        </Flex>
      </Flex>

      <Flex vertical gap="20px" className="graph-tiles team-activity pdf-tiles">
        <Flex
          align="center"
          className="graph-tiles__title"
          justify="space-between"
        >
          {entityLevel !== EntityLevel.Player ? (
            <Link
              className="report-link"
              to={`/${role}/${id}/assessment-report/gym`}
            >
              <h4 className="title">Gyms - Fact Fluency</h4>
            </Link>
          ) : (
            <h4 className="title">Gyms - Fact Fluency</h4>
          )}
          <div className="right">
            {entityLevel !== EntityLevel.Player && (
              <span className="print-hidden">
                <Link
                  to={`/${role}/${id}/assessment-report/gym`}
                  state={{ level }}
                >
                  <Tooltip
                    classNames={{ root: 'progress-tooltip' }}
                    title={
                      <div>
                        Click here to drill down into the Gyms report for this{' '}
                        {role}
                      </div>
                    }
                    arrow={false}
                    placement="bottom"
                  >
                    <img
                      src={images.Gyms}
                      alt="Gyms"
                      className="goal-index-report-icon"
                    />
                  </Tooltip>
                </Link>
                <Link to={`/${role}/${id}/assessment-report/just-the-facts`}>
                  <Tooltip
                    classNames={{ root: 'progress-tooltip' }}
                    title={
                      <div>
                        Click here to drill down into the Just The Facts report
                        for this {role}
                      </div>
                    }
                    arrow={false}
                    placement="bottom"
                  >
                    <img
                      src={images.Jtf}
                      alt="Jtf"
                      className="goal-index-report-icon"
                    />
                  </Tooltip>
                </Link>
                <Link
                  to={`/${role}/${id}/assessment-report/very-important-facts`}
                >
                  <Tooltip
                    classNames={{ root: 'progress-tooltip' }}
                    title={
                      <div>
                        Click here to drill down into the Very Important Facts
                        report for this {role}
                      </div>
                    }
                    arrow={false}
                    placement="bottom"
                  >
                    <img
                      src={images.Vif}
                      alt="Vif"
                      className="goal-index-report-icon"
                    />
                  </Tooltip>
                </Link>
              </span>
            )}
            <LevelSelect<GymMode>
              value={level ?? undefined}
              onChange={(newLevel) => setLevel(newLevel)}
              options={modeOptions}
            />
          </div>
        </Flex>
        <Flex vertical align="center" className="graph-tiles__graph">
          <Row style={{ width: '100%' }}>
            <Col
              style={{
                padding: '10px'
              }}
              xs={{ span: 24 }}
              lg={{ span: 12 }}
            >
              <h6>Add</h6>
              <GymCharts
                backgroundColor={theme.colors.gyms}
                chartData={levelData.add}
                level={level}
                steps={steps}
              />
            </Col>
            <Col
              style={{
                padding: '10px'
              }}
              xs={{ span: 24 }}
              lg={{ span: 12 }}
            >
              <h6>Subtract</h6>
              <GymCharts
                backgroundColor={theme.colors.gyms}
                chartData={levelData.sub}
                level={level}
                steps={steps}
              />
            </Col>
          </Row>
          {level === GymMode.K2 ? null : (
            <Row style={{ width: '100%' }}>
              <Col
                style={{
                  padding: '10px'
                }}
                xs={{ span: 24 }}
                lg={{ span: 12 }}
              >
                <h6>Multiply</h6>
                <GymCharts
                  backgroundColor={theme.colors.gyms}
                  chartData={levelData.mul}
                  level={level}
                  steps={steps}
                />
              </Col>
              <Col
                style={{
                  padding: '10px'
                }}
                xs={{ span: 24 }}
                lg={{ span: 12 }}
              >
                <h6>Divide</h6>
                <GymCharts
                  backgroundColor={theme.colors.gyms}
                  chartData={levelData.div}
                  level={level}
                  steps={steps}
                />
              </Col>
            </Row>
          )}
        </Flex>
      </Flex>
    </>
  )
}

export default Gym
